// NeevCoinsShop.js — Spend NeevCoins on rewards
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { playSound, fireConfetti, fireMini } from '../../utils/appUtils';
import { supabase } from '../../supabase';

const SHOP_ITEMS = [
  // Spins
  {
    id: 'extra_spin',
    category: 'Lucky Spin',
    emoji: '🎰',
    name: 'Extra Lucky Spin',
    desc: 'Spin the wheel again today for a bonus reward!',
    cost: 50,
    color: '#FF9500',
    bg: '#FFF8E1',
    type: 'spin',
  },
  // City themes
  {
    id: 'theme_night',
    category: 'City Theme',
    emoji: '🌙',
    name: 'Night City Theme',
    desc: 'Switch your NeevStar city to permanent night mode — looks epic!',
    cost: 100,
    color: '#8B2BE2',
    bg: '#F3E8FF',
    type: 'theme',
    value: 'night',
  },
  {
    id: 'theme_sunset',
    category: 'City Theme',
    emoji: '🌅',
    name: 'Sunset City Theme',
    desc: 'Bathe your city in golden sunset colours every time you visit.',
    cost: 100,
    color: '#FF6B00',
    bg: '#FFF3E0',
    type: 'theme',
    value: 'sunset',
  },
  {
    id: 'theme_space',
    category: 'City Theme',
    emoji: '🚀',
    name: 'Space City Theme',
    desc: 'Float your city among the stars — a galactic dark blue look.',
    cost: 150,
    color: '#0066FF',
    bg: '#EEF4FF',
    type: 'theme',
    value: 'space',
  },
  // XP boosts
  {
    id: 'xp_boost_1',
    category: 'XP Boost',
    emoji: '⚡',
    name: '2× XP Boost (1 session)',
    desc: 'Double XP on your next learning session — use it wisely!',
    cost: 75,
    color: '#00C851',
    bg: '#E8FAF0',
    type: 'xp_boost',
    value: 1,
  },
  {
    id: 'xp_boost_3',
    category: 'XP Boost',
    emoji: '🔥',
    name: '2× XP Boost (3 sessions)',
    desc: 'Double XP for your next 3 sessions. Go on a learning spree!',
    cost: 180,
    color: '#FF2D55',
    bg: '#FFE8EC',
    type: 'xp_boost',
    value: 3,
  },
  // Gifting
  {
    id: 'gift_friend',
    category: 'Gift',
    emoji: '🎁',
    name: 'Gift a Friend +50 XP',
    desc: 'Send 50 XP to a friend as a gift. They\'ll love you for it! 💛',
    cost: 100,
    color: '#00BCD4',
    bg: '#E0F5F7',
    type: 'gift',
  },
  // Streak shield
  {
    id: 'streak_shield',
    category: 'Protection',
    emoji: '🛡️',
    name: 'Streak Shield (1 day)',
    desc: 'Miss one day without breaking your streak. Your streak is safe!',
    cost: 120,
    color: '#FFD600',
    bg: '#FFFDE7',
    type: 'streak_shield',
  },
  // NeevBot name
  {
    id: 'custom_name',
    category: 'NeevBot',
    emoji: '✏️',
    name: 'Name Your NeevBot',
    desc: 'Give your NeevBot a custom name that shows in your city!',
    cost: 200,
    color: '#8B2BE2',
    bg: '#F3E8FF',
    type: 'bot_name',
  },
];

const CATEGORIES = ['All', 'Lucky Spin', 'City Theme', 'XP Boost', 'Protection', 'Gift', 'NeevBot'];

export default function NeevCoinsShop({ coins, onSpendCoins, onOpenSpin, profile, onClose }) {
  const [cat, setCat] = useState('All');
  const [bought, setBought] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`ns_shop_${profile?.parent_email}`) || '{}'); }
    catch { return {}; }
  });
  const [confirm, setConfirm] = useState(null); // item being confirmed
  const [botNameInput, setBotNameInput] = useState('');
  const [giftUsername, setGiftUsername] = useState('');
  const [giftStatus, setGiftStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [toast, setToast] = useState(null);

  const saveBought = (newBought) => {
    setBought(newBought);
    localStorage.setItem(`ns_shop_${profile?.parent_email}`, JSON.stringify(newBought));
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const purchase = (item) => {
    if (coins < item.cost) return;

    if (item.type === 'bot_name') {
      setConfirm(item);
      return;
    }

    setConfirm(item);
  };

  const confirmPurchase = (item, extra = null) => {
    setConfirm(null);
    onSpendCoins(item.cost);
    playSound('levelup');
    fireMini();

    const newBought = { ...bought };

    switch (item.type) {
      case 'spin':
        // Give an extra spin — store in localStorage
        const spins = parseInt(localStorage.getItem(`ns_extra_spins_${profile?.parent_email}`) || '0', 10);
        localStorage.setItem(`ns_extra_spins_${profile?.parent_email}`, String(spins + 1));
        showToast('🎰 Extra spin added! Open Lucky Spin to use it.');
        break;

      case 'theme':
        localStorage.setItem(`ns_city_theme_${profile?.parent_email}`, item.value);
        newBought[item.id] = true;
        saveBought(newBought);
        showToast(`${item.emoji} ${item.name} applied to your city!`);
        break;

      case 'xp_boost':
        const existing = parseInt(localStorage.getItem(`ns_xp_boost_${profile?.parent_email}`) || '0', 10);
        localStorage.setItem(`ns_xp_boost_${profile?.parent_email}`, String(existing + item.value));
        newBought[item.id] = (newBought[item.id] || 0) + 1;
        saveBought(newBought);
        showToast(`⚡ 2× XP Boost activated for ${item.value} session${item.value > 1 ? 's' : ''}!`);
        break;

      case 'streak_shield':
        const shields = parseInt(localStorage.getItem(`ns_streak_shields_${profile?.parent_email}`) || '0', 10);
        localStorage.setItem(`ns_streak_shields_${profile?.parent_email}`, String(shields + 1));
        newBought[item.id] = (newBought[item.id] || 0) + 1;
        saveBought(newBought);
        showToast('🛡️ Streak Shield saved! You can miss 1 day safely.');
        break;

      case 'gift':
        // Actually send +50 XP to the friend via Supabase
        setGiftStatus('sending');
        supabase.from('bm_child_profiles')
          .select('xp_points,child_name,parent_email')
          .eq('neev_username', extra?.trim() || giftUsername.trim())
          .single()
          .then(({ data: friend }) => {
            if (!friend) { setGiftStatus('error'); showToast('❌ Username not found. Check spelling!'); return; }
            const newXP = (friend.xp_points || 0) + 50;
            supabase.from('bm_child_profiles')
              .update({ xp_points: newXP })
              .eq('parent_email', friend.parent_email)
              .then(() => {
                setGiftStatus('sent');
                newBought[item.id] = (newBought[item.id] || 0) + 1;
                saveBought(newBought);
                showToast(`🎁 +50 XP gifted to ${friend.child_name}! They'll love you! 💛`);
                setGiftUsername('');
                fireConfetti();
              }).catch(() => { setGiftStatus('error'); showToast('❌ Could not send gift. Try again.'); });
          }).catch(() => { setGiftStatus('error'); showToast('❌ Username not found. Check spelling!'); });
        break;

      case 'bot_name':
        const name = extra || botNameInput;
        if (name.trim()) {
          localStorage.setItem(`ns_bot_name_${profile?.parent_email}`, name.trim());
          newBought[item.id] = true;
          saveBought(newBought);
          showToast(`✏️ Your NeevBot is now "${name.trim()}"!`);
        }
        break;

      default:
        break;
    }

    if (item.type === 'theme') fireConfetti();
  };

  const filtered = cat === 'All' ? SHOP_ITEMS : SHOP_ITEMS.filter(i => i.category === cat);

  // Check active boosts
  const activeBoosts = parseInt(localStorage.getItem(`ns_xp_boost_${profile?.parent_email}`) || '0', 10);
  const activeShields = parseInt(localStorage.getItem(`ns_streak_shields_${profile?.parent_email}`) || '0', 10);
  const extraSpins = parseInt(localStorage.getItem(`ns_extra_spins_${profile?.parent_email}`) || '0', 10);
  const botName = localStorage.getItem(`ns_bot_name_${profile?.parent_email}`);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.92)',
      zIndex: 9800, display: 'flex', flexDirection: 'column',
      fontFamily: "'Nunito', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg,#1A1A2E,#2A1A4E)',
        padding: '16px 16px 0', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: 'white' }}>🛍️ NeevCoins Shop</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>
              Spend your coins on cool rewards!
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              background: 'linear-gradient(135deg,#FFD600,#FF9500)',
              borderRadius: 20, padding: '8px 16px', display: 'inline-flex',
              alignItems: 'center', gap: 6,
            }}>
              <span style={{ fontSize: 18 }}>🪙</span>
              <span style={{ fontSize: 18, fontWeight: 900, color: '#1A1A2E' }}>{coins}</span>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 700 }}>
              your coins
            </p>
          </div>
        </div>

        {/* Active items bar */}
        {(activeBoosts > 0 || activeShields > 0 || extraSpins > 0 || botName) && (
          <div style={{
            display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap',
          }}>
            {activeBoosts > 0 && <span style={{ background: '#E8FAF0', color: '#00C851', borderRadius: 10, padding: '3px 10px', fontSize: 11, fontWeight: 900 }}>⚡ {activeBoosts} XP Boost{activeBoosts > 1 ? 's' : ''} active</span>}
            {activeShields > 0 && <span style={{ background: '#FFFDE7', color: '#FF9500', borderRadius: 10, padding: '3px 10px', fontSize: 11, fontWeight: 900 }}>🛡️ {activeShields} Shield{activeShields > 1 ? 's' : ''}</span>}
            {extraSpins > 0 && <span style={{ background: '#FFF8E1', color: '#FF9500', borderRadius: 10, padding: '3px 10px', fontSize: 11, fontWeight: 900 }}>🎰 {extraSpins} extra spin{extraSpins > 1 ? 's' : ''}</span>}
            {botName && <span style={{ background: '#F3E8FF', color: '#8B2BE2', borderRadius: 10, padding: '3px 10px', fontSize: 11, fontWeight: 900 }}>🤖 {botName}</span>}
          </div>
        )}

        {/* Category tabs */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 12 }}>
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCat(c)} style={{
              flexShrink: 0, padding: '6px 14px', borderRadius: 20,
              border: 'none', cursor: 'pointer', fontFamily: "'Nunito', sans-serif",
              fontWeight: 800, fontSize: 12, whiteSpace: 'nowrap',
              background: cat === c ? 'linear-gradient(135deg,#FFD600,#FF9500)' : 'rgba(255,255,255,0.1)',
              color: cat === c ? '#1A1A2E' : 'rgba(255,255,255,0.7)',
            }}>{c}</button>
          ))}
        </div>
      </div>

      {/* Items grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 100px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {filtered.map(item => {
            const canAfford = coins >= item.cost;
            const isOwned = bought[item.id] === true;
            const count = typeof bought[item.id] === 'number' ? bought[item.id] : null;

            return (
              <motion.div key={item.id}
                whileTap={canAfford ? { scale: 0.97 } : {}}
                onClick={() => canAfford && !isOwned && purchase(item)}
                style={{
                  background: isOwned ? '#F0FFF4' : item.bg,
                  borderRadius: 18, padding: '14px 12px',
                  border: `2px solid ${canAfford && !isOwned ? item.color + '55' : '#E8E8E8'}`,
                  cursor: canAfford && !isOwned ? 'pointer' : 'default',
                  opacity: canAfford ? 1 : 0.6,
                  position: 'relative',
                  transition: 'opacity 0.2s',
                }}
              >
                {/* Category badge */}
                <span style={{
                  position: 'absolute', top: 8, right: 8,
                  background: item.color + '22', color: item.color,
                  borderRadius: 8, padding: '2px 6px', fontSize: 9, fontWeight: 900,
                }}>{item.category}</span>

                <div style={{ fontSize: 36, marginBottom: 8 }}>{item.emoji}</div>
                <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 900, color: '#1A1A2E', lineHeight: 1.3 }}>
                  {item.name}
                </p>
                <p style={{ margin: '0 0 10px', fontSize: 11, color: '#888', fontWeight: 700, lineHeight: 1.4 }}>
                  {item.desc}
                </p>

                {isOwned ? (
                  <div style={{
                    background: '#00C85122', borderRadius: 10, padding: '6px',
                    textAlign: 'center', fontSize: 12, fontWeight: 900, color: '#00C851',
                  }}>✅ Owned</div>
                ) : (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: canAfford ? item.color : '#E8E8E8',
                    borderRadius: 10, padding: '8px 10px',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 900, color: canAfford ? 'white' : '#AAA' }}>
                      🪙 {item.cost}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 800, color: canAfford ? 'rgba(255,255,255,0.85)' : '#BBB' }}>
                      {canAfford ? 'Buy →' : 'Need more coins'}
                    </span>
                  </div>
                )}

                {count !== null && count > 0 && (
                  <div style={{
                    marginTop: 6, background: item.color + '22', borderRadius: 8,
                    padding: '3px 8px', fontSize: 11, fontWeight: 900, color: item.color,
                    textAlign: 'center',
                  }}>× {count} owned</div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* How to earn more */}
        <div style={{
          background: 'rgba(255,255,255,0.07)', borderRadius: 16,
          padding: '14px', marginTop: 16,
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 900, color: 'rgba(255,255,255,0.8)' }}>
            🪙 How to earn more coins
          </p>
          {[
            ['Answer questions correctly', '+5 coins each'],
            ['Complete a daily challenge', '+25 coins'],
            ['Spin the Lucky Wheel', 'Up to +100 coins'],
            ['Open a streak chest', '+15-100 coins'],
            ['Earn a certificate', '+50 coins'],
          ].map(([action, reward]) => (
            <div key={action} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>{action}</span>
              <span style={{ fontSize: 12, color: '#FFD600', fontWeight: 900 }}>{reward}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Close button */}
      <div style={{
        position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9810,
      }}>
        <button onClick={onClose} style={{
          padding: '14px 40px', borderRadius: 50, border: 'none',
          background: 'rgba(255,255,255,0.15)', color: 'white',
          fontSize: 15, fontWeight: 900, cursor: 'pointer',
          fontFamily: "'Nunito', sans-serif",
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>✕ Close Shop</button>
      </div>

      {/* Confirm modal */}
      <AnimatePresence>
        {confirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
              zIndex: 9820, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
            }}>
            <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              style={{
                background: 'white', borderRadius: 24, padding: '24px 20px',
                maxWidth: 360, width: '100%', textAlign: 'center',
                fontFamily: "'Nunito', sans-serif",
              }}>
              <p style={{ fontSize: 52, margin: '0 0 12px' }}>{confirm.emoji}</p>
              <h3 style={{ fontSize: 20, fontWeight: 900, color: '#1A1A2E', margin: '0 0 6px' }}>
                Buy {confirm.name}?
              </h3>
              <p style={{ fontSize: 13, color: '#888', fontWeight: 700, margin: '0 0 16px', lineHeight: 1.6 }}>
                {confirm.desc}
              </p>

              {confirm.type === 'bot_name' && (
                <input
                  value={botNameInput}
                  onChange={e => setBotNameInput(e.target.value.slice(0, 16))}
                  placeholder="Enter a name (max 16 chars)"
                  autoFocus
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 12,
                    border: '2px solid #E8E8E8', fontSize: 15, fontWeight: 800,
                    fontFamily: "'Nunito', sans-serif", outline: 'none',
                    marginBottom: 14, boxSizing: 'border-box', textAlign: 'center',
                  }}
                />
              )}
              {confirm.type === 'gift' && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 12, fontWeight: 800, color: '#888', margin: '0 0 6px' }}>
                    Enter your friend's NeevStar username
                  </p>
                  <input
                    value={giftUsername}
                    onChange={e => { setGiftUsername(e.target.value); setGiftStatus(null); }}
                    placeholder="e.g. NeevExplorer_4821"
                    autoFocus
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: 12,
                      border: `2px solid ${giftStatus === 'error' ? '#FF2D55' : '#E8E8E8'}`,
                      fontSize: 14, fontWeight: 800, fontFamily: "'Nunito', sans-serif",
                      outline: 'none', marginBottom: 0, boxSizing: 'border-box', textAlign: 'center',
                    }}
                  />
                  {giftStatus === 'error' && <p style={{ color: '#FF2D55', fontSize: 12, fontWeight: 700, margin: '4px 0 0' }}>Username not found</p>}
                </div>
              )}

              <div style={{
                background: '#FFF8E1', borderRadius: 12, padding: '10px',
                border: '2px solid #FFD600', marginBottom: 20,
              }}>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 900, color: '#FF9500' }}>
                  🪙 {confirm.cost} coins
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#888', fontWeight: 700 }}>
                  You'll have {coins - confirm.cost} coins left
                </p>
              </div>

              <motion.button whileTap={{ scale: 0.97 }}
                onClick={() => {
                  if (confirm.type === 'bot_name' && !botNameInput.trim()) return;
                  if (confirm.type === 'gift' && !giftUsername.trim()) return;
                  confirmPurchase(confirm, confirm.type === 'bot_name' ? botNameInput : confirm.type === 'gift' ? giftUsername : null);
                }}
                style={{
                  width: '100%', padding: '14px', borderRadius: 14, border: 'none',
                  background: `linear-gradient(135deg,${confirm.color},${confirm.color}BB)`,
                  color: 'white', fontSize: 16, fontWeight: 900, cursor: 'pointer',
                  marginBottom: 10, fontFamily: "'Nunito', sans-serif",
                }}>
                Confirm Purchase ✓
              </motion.button>
              <button onClick={() => setConfirm(null)} style={{
                width: '100%', padding: '12px', borderRadius: 14,
                border: 'none', background: '#F5F5F5', color: '#888',
                fontSize: 14, fontWeight: 800, cursor: 'pointer',
                fontFamily: "'Nunito', sans-serif",
              }}>Cancel</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            style={{
              position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
              background: '#1A1A2E', borderRadius: 14, padding: '12px 20px',
              maxWidth: 320, width: 'calc(100% - 32px)', zIndex: 9830,
              display: 'flex', alignItems: 'center', gap: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              fontFamily: "'Nunito', sans-serif",
            }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: 'white', flex: 1 }}>{toast}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
